<script setup lang="ts">
import { ref, nextTick, watch, onMounted, onUnmounted } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  useGeolocationStore,
  formatDistance,
  type NearbyExplosion,
  type NearbyTrajectory,
  type LatLon,
} from '@/modules/geolocation';
import { formatRelativeTime } from '@/utils/time';
import { trace } from '@/utils/onScreenLog';
import { EXPLOSION_CLASS_COLORS, EXPLOSION_CLASS_LABELS } from '@/types/geolocation';

/** Quality → visual weight: provisional trajectories are drawn faint/dashed (mirrors GeoMapView). */
const QUALITY_STYLE: Record<string, { opacity: number; dash?: string; label: string }> = {
  reliable: { opacity: 0.9, label: 'надёжно' },
  coarse: { opacity: 0.6, dash: '6 4', label: 'грубо' },
  provisional: { opacity: 0.35, dash: '2 6', label: 'предварительно' },
};

/** Corridor fill opacity by quality tier — only drawn for coarse/provisional (reliable needs no caveat). */
const CORRIDOR_FILL_OPACITY: Record<string, number> = {
  coarse: 0.1,
  provisional: 0.16,
};

const METERS_PER_DEG_LAT = 111_320;

/** Local equirectangular projection around a reference point — accurate enough at radar's ~20 km scale. */
function toLocalMeters(p: { lat: number; lon: number }, ref: { lat: number; lon: number }): [number, number] {
  const x = (p.lon - ref.lon) * METERS_PER_DEG_LAT * Math.cos((ref.lat * Math.PI) / 180);
  const y = (p.lat - ref.lat) * METERS_PER_DEG_LAT;
  return [x, y];
}

function fromLocalMeters(x: number, y: number, ref: { lat: number; lon: number }): L.LatLngExpression {
  const lat = ref.lat + y / METERS_PER_DEG_LAT;
  const lon = ref.lon + x / (METERS_PER_DEG_LAT * Math.cos((ref.lat * Math.PI) / 180));
  return [lat, lon];
}

/**
 * Build a widening uncertainty corridor around a reconstructed path: half-width at each vertex is
 * `posSigmaM` plus a heading-error cone that grows with distance from the path start (same idea as
 * the live track's forward prediction circle, but swept along the whole path instead of just one
 * forward projection). Makes `coarse`/`provisional` trajectories visibly uncertain without needing
 * to tap the popup. Returns a closed polygon ring, or null if the path is degenerate.
 */
function buildUncertaintyCorridor(
  path: Array<{ lat: number; lon: number }>,
  posSigmaM: number,
  headingSigmaDeg: number,
): L.LatLngExpression[] | null {
  if (path.length < 2) return null;
  const ref = path[0];
  const pts = path.map((p) => toLocalMeters(p, ref));
  const headingSigmaRad = (Math.min(Math.max(headingSigmaDeg, 0), 90) * Math.PI) / 180;

  let dist = 0;
  const left: L.LatLngExpression[] = [];
  const right: L.LatLngExpression[] = [];
  for (let i = 0; i < pts.length; i++) {
    const prev = pts[Math.max(i - 1, 0)];
    const next = pts[Math.min(i + 1, pts.length - 1)];
    let dx = next[0] - prev[0];
    let dy = next[1] - prev[1];
    const len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;
    if (i > 0) dist += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);

    // Perpendicular to the direction of travel, capped so a huge sigma doesn't blow up the map.
    const halfWidth = Math.min(posSigmaM + dist * Math.tan(headingSigmaRad), 8000);
    const px = -dy * halfWidth;
    const py = dx * halfWidth;
    left.push(fromLocalMeters(pts[i][0] + px, pts[i][1] + py, ref));
    right.push(fromLocalMeters(pts[i][0] - px, pts[i][1] - py, ref));
  }
  return [...left, ...right.reverse()];
}

/** Track status → opacity + label for the live moving-object layer (mirrors GeoMapView). */
const TRACK_STATUS_STYLE: Record<string, { opacity: number; label: string }> = {
  confirmed: { opacity: 0.95, label: 'подтверждён' },
  tentative: { opacity: 0.6, label: 'кандидат' },
  coasting: { opacity: 0.5, label: 'экстраполяция' },
  terminated: { opacity: 0.2, label: 'завершён' },
};

/** Quadrant side in degrees of latitude (~2 km). Mirrors backend QUADRANT_LAT_DELTA. */
const QUADRANT_LAT_DELTA = 2000 / 111320;

/** Quadrant side in degrees of longitude (double of latitude delta). Mirrors backend QUADRANT_LON_DELTA. */
const QUADRANT_LON_DELTA = QUADRANT_LAT_DELTA * 2;

/**
 * Карта радара: владеет Leaflet-инстансом и его жизненным циклом (создание,
 * invalidateSize при показе, remove при размонтировании), маркерами событий
 * и отметкой «вы здесь». Императивный API через defineExpose — страница
 * командует центром/маркерами, тап в режиме ручной отметки уходит emit'ом.
 */

const props = defineProps<{
  showMap: boolean;
  showQuadrants: boolean;
  manualMode: boolean;
  accuracy: number | null;
  gpsError: string | null;
}>();

const emit = defineEmits<{
  pick: [lat: number, lon: number];
  'retry-location': [];
}>();

const store = useGeolocationStore();

const gpsDismissed = ref(false);
watch(
  () => props.gpsError,
  (err) => {
    if (err) gpsDismissed.value = false;
  },
);

const mapContainer = ref<HTMLDivElement>();
let map: L.Map | null = null;
let youLayer: L.LayerGroup | null = null;
let markers: L.LayerGroup | null = null;
let quadrantLayer: L.LayerGroup | null = null;
let explosionLayer: L.LayerGroup | null = null;
let trajLayer: L.LayerGroup | null = null;
let trackLayer: L.LayerGroup | null = null;

/**
 * Live index of rendered explosion markers, keyed by stable explosion id. Lets updateMarkers()
 * diff against the store instead of clearing and recreating every marker on each update (WS
 * cluster-update, region pull, the 30 s tick). `data` holds the latest explosion so the lazy
 * popup always shows fresh distance/time without rebuilding the marker.
 */
interface MarkerEntry {
  marker: L.Marker;
  lat: number;
  lon: number;
  className: string;
  data: NearbyExplosion;
}
const markerIndex = new Map<string, MarkerEntry>();

/** Zoom level the map snaps to when manual placement is armed, so a point can be picked precisely. */
const MANUAL_PICK_ZOOM = 15;

function init(center: LatLon, markUser = true): void {
  trace.log('[LiveRadar][initMap] called', {
    center,
    markUser,
    hasContainer: !!mapContainer.value,
    alreadyInited: !!map,
  });
  if (!mapContainer.value) {
    trace.warn('[LiveRadar][initMap] ABORT: mapContainer ref is null (DOM not mounted / v-show hidden?)');
    return;
  }
  if (map) {
    trace.warn('[LiveRadar][initMap] ABORT: map already initialised');
    return;
  }

  try {
    const rect = mapContainer.value.getBoundingClientRect();
    trace.log('[LiveRadar][initMap] container size', {
      width: rect.width,
      height: rect.height,
      offsetParent: !!mapContainer.value.offsetParent,
    });

    map = L.map(mapContainer.value, { zoomControl: false, attributionControl: false }).setView(
      [center.lat, center.lon],
      markUser ? 11 : 8,
    );
    trace.log('[LiveRadar][initMap] L.map created OK');

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map);
    trace.log('[LiveRadar][initMap] tileLayer added OK');

    youLayer = L.layerGroup().addTo(map);
    markers = L.layerGroup().addTo(map);
    quadrantLayer = L.layerGroup().addTo(map);
    explosionLayer = L.layerGroup().addTo(map);
    trajLayer = L.layerGroup().addTo(map);
    trackLayer = L.layerGroup().addTo(map);

    // Tap-to-place: when manual mode is armed, a click sets the user position by hand.
    map.on('click', onMapClick);

    // Only mark the user when we actually know where they are — a default/placeholder
    // centre must not pin a misleading "you are here".
    if (markUser) drawYou(center);

    // Leaflet often mis-measures a container that was hidden / sized late (flex, v-show, mobile
    // address-bar collapse). Force a re-layout on the next frame so tiles fill the box.
    nextTick(() => {
      trace.log('[LiveRadar][initMap] invalidateSize after nextTick', {
        size: map ? [map.getSize().x, map.getSize().y] : null,
      });
      map?.invalidateSize();
    });
    trace.log('[LiveRadar][initMap] DONE');
  } catch (e) {
    trace.error('[LiveRadar][initMap] THREW', e);
    map = null;
  }
}

function onMapClick(e: L.LeafletMouseEvent): void {
  if (!props.manualMode) return;
  emit('pick', e.latlng.lat, e.latlng.lng);
}

function drawYou(center: LatLon): void {
  if (!map || !youLayer) return;
  youLayer.clearLayers();

  const icon = L.divIcon({
    className: '',
    html: `<div class="you-dot"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
  L.marker([center.lat, center.lon], { icon }).addTo(youLayer);

  if (props.accuracy && props.accuracy > 0) {
    L.circle([center.lat, center.lon], {
      radius: props.accuracy,
      color: '#2aabee',
      weight: 1,
      fillColor: '#2aabee',
      fillOpacity: 0.18,
    }).addTo(youLayer);
  }
}

function recenter(center: LatLon, keepZoom = false): void {
  if (!map) return;
  drawYou(center);
  if (keepZoom) {
    map.setView([center.lat, center.lon], map.getZoom(), { animate: false });
  } else {
    map.setView([center.lat, center.lon], 11);
  }
}

/** Единая точка «центр изменился»: создаёт карту при первом вызове, дальше — recenter. */
function setCenter(center: LatLon, keepZoom = false): void {
  if (!map) {
    init(center);
  } else {
    recenter(center, keepZoom);
  }
  updateMarkers();
  updateTrajectories();
  updateTracks();
}

function makeExplosionIcon(className: string): L.DivIcon {
  const color = EXPLOSION_CLASS_COLORS[className as keyof typeof EXPLOSION_CLASS_COLORS] || '#e74c3c';
  return L.divIcon({
    className: '',
    html: `<div style="width:16px;height:16px;background:${color};border:2px solid #fff;border-radius:50%;box-shadow:0 0 8px ${color}aa;"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

/** Popup HTML for one explosion. Built lazily on open so distance/relative time stay current. */
function explosionPopupHtml(e: NearbyExplosion): string {
  return (
    `<div style="font-family:system-ui;font-size:13px;min-width:140px;">` +
    `<strong>${EXPLOSION_CLASS_LABELS[e.className] || e.className}</strong><br>` +
    `<span style="color:#888;">${formatDistance(e.distanceM)} · ${formatRelativeTime(e.createdAt)}</span>` +
    `</div>`
  );
}

/**
 * Diff the rendered markers against store.nearbyExplosions: keep unchanged markers, move/re-icon
 * those that changed (re-triangulation), add new ones, drop the gone. Avoids the full clear +
 * recreate that ran on every WS event and the 30 s tick.
 */
function updateMarkers(): void {
  if (!markers) return;

  const seen = new Set<string>();
  for (const e of store.nearbyExplosions) {
    seen.add(e.id);
    const existing = markerIndex.get(e.id);
    if (existing) {
      existing.data = e; // refresh for the lazy popup
      if (existing.lat !== e.latitude || existing.lon !== e.longitude) {
        existing.marker.setLatLng([e.latitude, e.longitude]);
        existing.lat = e.latitude;
        existing.lon = e.longitude;
      }
      if (existing.className !== e.className) {
        existing.marker.setIcon(makeExplosionIcon(e.className));
        existing.className = e.className;
      }
    } else {
      const marker = L.marker([e.latitude, e.longitude], { icon: makeExplosionIcon(e.className) }).addTo(markers!);
      const entry: MarkerEntry = {
        marker,
        lat: e.latitude,
        lon: e.longitude,
        className: e.className,
        data: e,
      };
      // Lazy popup content: rebuilt from the latest `entry.data` each time it opens.
      marker.bindPopup(() => explosionPopupHtml(entry.data));
      markerIndex.set(e.id, entry);
    }
  }

  // Remove markers whose explosion is no longer in range.
  for (const [id, entry] of markerIndex) {
    if (!seen.has(id)) {
      markers.removeLayer(entry.marker);
      markerIndex.delete(id);
    }
  }
}

function focusEvent(e: NearbyExplosion): void {
  if (!map) return;
  map.setView([e.latitude, e.longitude], 13);
}

/**
 * Draw nearby drone trajectories: the reconstructed path (curve preferred over the straight fit) +
 * a heading arrow at the path end + a popup with course/speed/quality. Clear-and-redraw — there are
 * only a handful of nearby trajectories, so diffing isn't worth it. Ported from GeoMapView.
 */
function updateTrajectories(): void {
  if (!trajLayer) return;
  trajLayer.clearLayers();

  for (const t of store.nearbyTrajectories) {
    const color = EXPLOSION_CLASS_COLORS[t.className] || '#9b59b6';
    const style = QUALITY_STYLE[t.quality] ?? QUALITY_STYLE.provisional;
    const rawPath = t.curvePolyline?.length ? t.curvePolyline : t.polyline;
    const path = rawPath.length ? rawPath : [{ lat: t.originLat, lon: t.originLon }];
    const pts: L.LatLngExpression[] = path.map((p) => [p.lat, p.lon] as L.LatLngExpression);

    const corridorFillOpacity = CORRIDOR_FILL_OPACITY[t.quality];
    if (corridorFillOpacity != null) {
      const corridor = buildUncertaintyCorridor(path, t.posSigmaM, t.headingSigmaDeg);
      if (corridor) {
        L.polygon(corridor, {
          stroke: false,
          fillColor: color,
          fillOpacity: corridorFillOpacity,
        }).addTo(trajLayer!);
      }
    }

    if (pts.length >= 2) {
      L.polyline(pts, { color, weight: 3, opacity: style.opacity, dashArray: style.dash }).addTo(trajLayer!);
    }

    // Heading arrow at the path end (➤ points east ⇒ rotate by heading−90°).
    const head = pts[pts.length - 1];
    const arrow = L.divIcon({
      className: '',
      html: `<div style="color:${color};font-size:18px;line-height:1;opacity:${style.opacity};transform:rotate(${t.headingDeg - 90}deg);transform-origin:center;">➤</div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    });
    const marker = L.marker(head, { icon: arrow }).addTo(trajLayer!);
    marker.bindPopup(
      `<div style="min-width:190px;font-family:monospace;font-size:12px;">` +
        `<strong style="font-size:14px;">${EXPLOSION_CLASS_LABELS[t.className] || t.className} (траектория)</strong><br>` +
        `<span style="color:#888;">${formatDistance(t.distanceM)} · ${formatRelativeTime(t.createdAt)}</span>` +
        `<hr style="margin:4px 0;border-color:#eee;">` +
        `<b>Курс:</b> ${t.headingDeg.toFixed(0)}°<br>` +
        `<b>Скорость:</b> ${t.speedMps.toFixed(0)} м/с (${(t.speedMps * 3.6).toFixed(0)} км/ч)<br>` +
        `<b>Качество:</b> ${style.label}${t.converged ? '' : ' (не сошлось)'}<br>` +
        `<b>±курс:</b> ${t.headingSigmaDeg.toFixed(0)}°  <b>±поз:</b> ${t.posSigmaM.toFixed(0)}м<br>` +
        `<b>Датчиков:</b> ${t.sensorCount}` +
        `</div>`,
    );
  }
}

/**
 * Draw nearby live moving-object tracks: the solid `history` polyline of where each object has
 * been, a faint dashed `predicted` dead-reckoning cone (+ uncertainty circle at the tip), and a
 * pulsing marker at the current position rotated to the heading. One marker per track, moving as
 * updates arrive. Ported from GeoMapView.
 */
function updateTracks(): void {
  if (!trackLayer) return;
  trackLayer.clearLayers();

  for (const t of store.nearbyTracks) {
    const color = EXPLOSION_CLASS_COLORS[t.className] || '#9b59b6';
    const style = TRACK_STATUS_STYLE[t.status] ?? TRACK_STATUS_STYLE.tentative;

    const hist: L.LatLngExpression[] = t.history.map((p) => [p.lat, p.lon]);
    if (hist.length >= 2) {
      L.polyline(hist, { color, weight: 3, opacity: style.opacity }).addTo(trackLayer!);
    }

    if (t.predicted.length >= 1) {
      const cone: L.LatLngExpression[] = [
        [t.lat, t.lon],
        ...t.predicted.map((p) => [p.lat, p.lon] as L.LatLngExpression),
      ];
      L.polyline(cone, { color, weight: 2, opacity: style.opacity * 0.6, dashArray: '4 6' }).addTo(trackLayer!);
      const tip = t.predicted[t.predicted.length - 1];
      if (tip.sigmaM > 0) {
        L.circle([tip.lat, tip.lon], {
          radius: tip.sigmaM,
          color,
          weight: 1,
          opacity: style.opacity * 0.5,
          fillColor: color,
          fillOpacity: 0.05,
          dashArray: '3 5',
        }).addTo(trackLayer!);
      }
    }

    const pulse = t.status === 'coasting' ? '' : 'track-pulse';
    const icon = L.divIcon({
      className: '',
      html:
        `<div class="track-head ${pulse}" style="opacity:${style.opacity};">` +
        `<div style="color:${color};font-size:20px;line-height:1;transform:rotate(${t.headingDeg - 90}deg);transform-origin:center;filter:drop-shadow(0 0 4px ${color});">➤</div>` +
        `</div>`,
      iconSize: [22, 22],
      iconAnchor: [11, 11],
    });
    const marker = L.marker([t.lat, t.lon], { icon }).addTo(trackLayer!);
    marker.bindPopup(
      `<div style="min-width:200px;font-family:monospace;font-size:12px;">` +
        `<strong style="font-size:14px;">${EXPLOSION_CLASS_LABELS[t.className] || t.className} (трек)</strong><br>` +
        `<span style="color:#888;">${formatDistance(t.distanceM)} · ${formatRelativeTime(t.lastUpdateMs)}</span>` +
        `<hr style="margin:4px 0;border-color:#eee;">` +
        `<b>Статус:</b> ${style.label}<br>` +
        `<b>Курс:</b> ${t.headingDeg.toFixed(0)}°<br>` +
        `<b>Скорость:</b> ${t.speedMps.toFixed(0)} м/с (${(t.speedMps * 3.6).toFixed(0)} км/ч)<br>` +
        `<b>±поз:</b> ${t.posSigmaM.toFixed(0)}м<br>` +
        (t.maneuverProb > 0 ? `<b>Манёвр:</b> ${(t.maneuverProb * 100).toFixed(0)}% (IMM)<br>` : '') +
        `<b>Хитов:</b> ${t.hits}` +
        `</div>`,
    );
  }
}

/** Fit the map to a trajectory's path (clicked from the event feed). */
function focusTrajectory(t: NearbyTrajectory): void {
  if (!map) return;
  const path = t.curvePolyline?.length ? t.curvePolyline : t.polyline;
  if (path.length >= 2) {
    map.fitBounds(L.latLngBounds(path.map((p) => [p.lat, p.lon] as L.LatLngExpression)), {
      padding: [40, 40],
      maxZoom: 13,
    });
  } else {
    map.setView([t.originLat, t.originLon], 12);
  }
}

/**
 * Zoom in to a detailed level around `c` (or the current centre) so a manual point can be
 * placed precisely. Never zooms *out* if the user is already closer in.
 */
function zoomForPick(c: LatLon | null): void {
  if (!map) return;
  const focus = c ?? { lat: map.getCenter().lat, lon: map.getCenter().lng };
  map.setView([focus.lat, focus.lon], Math.max(map.getZoom(), MANUAL_PICK_ZOOM));
}

  function updateQuadrants(): void {
    if (!quadrantLayer) return;
    quadrantLayer.clearLayers();

    if (!props.showQuadrants) {
      trace.log('[LiveRadar][updateQuadrants] skipped — showQuadrants=false');
      return;
    }

    const quads = store.monitorQuadrants;
    trace.lazy(() => {
      const mapCenter = map ? map.getCenter() : null;
      return [
        '[LiveRadar][updateQuadrants]',
        {
          quadrantCount: quads.length,
          mapCenter: mapCenter ? { lat: +mapCenter.lat.toFixed(5), lng: +mapCenter.lng.toFixed(5) } : null,
          mapZoom: map?.getZoom() ?? null,
          quadrants: quads.map((q) => ({
            row: q.row,
            col: q.col,
            count: q.count,
            bounds: {
              south: +(q.row * QUADRANT_LAT_DELTA).toFixed(5),
              west: +(q.col * QUADRANT_LON_DELTA).toFixed(5),
              north: +((q.row + 1) * QUADRANT_LAT_DELTA).toFixed(5),
              east: +((q.col + 1) * QUADRANT_LON_DELTA).toFixed(5),
            },
          })),
        },
      ];
    });

    for (const q of quads) {
      const qLat = q.row * QUADRANT_LAT_DELTA;
      const qLon = q.col * QUADRANT_LON_DELTA;
      L.rectangle(
        [
          [qLat, qLon],
          [qLat + QUADRANT_LAT_DELTA, qLon + QUADRANT_LON_DELTA],
        ],
        {
          color: '#FFD700',
          weight: 0.5,
          fillColor: '#FFD700',
          fillOpacity: 0.4,
        },
      ).addTo(quadrantLayer);
    }
  }

  function highlightExplosionQuadrant(row: number, col: number, count: number): void {
    if (!explosionLayer) return;
    trace.log('[LiveRadar][highlightExplosionQuadrant]', { row, col, count });
    const qLat = row * QUADRANT_LAT_DELTA;
    const qLon = col * QUADRANT_LON_DELTA;
    const rect = L.rectangle(
      [
        [qLat, qLon],
        [qLat + QUADRANT_LAT_DELTA, qLon + QUADRANT_LON_DELTA],
      ],
      {
        color: '#ff4444',
        weight: 1,
        fillColor: '#ff0000',
        fillOpacity: 0.5,
      },
    ).addTo(explosionLayer!);

    setTimeout(() => {
      rect.remove();
      store.removeExplosionQuadrant(row, col);
    }, 30_000);
  }

  // Leaflet mis-sizes itself while hidden — recompute the layout once it's visible again.
watch(
  () => props.showMap,
  (on) => {
    if (on) nextTick(() => map?.invalidateSize());
  },
);

// Recompute Leaflet's internal size whenever the container is resized — covers the draggable
// splitter (and any window/panel resize). invalidateSize can fire many times during a drag, so we
// let Leaflet's own debounce do the work and just throttle to one call per animation frame.
let resizeObs: ResizeObserver | null = null;
let resizeRaf = 0;
onMounted(() => {
  if (typeof ResizeObserver === 'undefined' || !mapContainer.value) return;
  resizeObs = new ResizeObserver(() => {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      map?.invalidateSize();
    });
  });
  resizeObs.observe(mapContainer.value);
});

watch(
  () => props.showQuadrants,
  () => updateQuadrants(),
);

onUnmounted(() => {
  if (map) {
    map.remove();
    map = null;
  }
  markerIndex.clear();
  if (resizeRaf) cancelAnimationFrame(resizeRaf);
  resizeObs?.disconnect();
  resizeObs = null;
});

defineExpose({
  init,
  setCenter,
  updateMarkers,
  updateQuadrants,
  updateTrajectories,
  updateTracks,
  highlightExplosionQuadrant,
  focusEvent,
  focusTrajectory,
  zoomForPick,
});
</script>

<template>
  <!-- Left column: the map -->
  <div
    class="map-col"
    data-onboarding-target="map-area"
  >
    <!-- Mini map: главное на экране — карта рядом с активностью микрофона -->
    <div
      v-show="showMap"
      class="map-wrap"
    >
      <div
        ref="mapContainer"
        class="mini-map"
        :class="{ picking: manualMode }"
      />
      <div
        v-if="manualMode"
        class="pick-hint"
      >
        Коснитесь карты, чтобы отметить себя
      </div>
    </div>

    <!-- GPS prompt -->
    <div
      v-if="gpsError && !gpsDismissed"
      class="gps-prompt"
    >
      <span>📍 Нет доступа к геолокации — разрешите её или отметьте себя на карте вручную.</span>
      <div class="gps-prompt-actions">
        <button @click="emit('retry-location')">
          Разрешить
        </button>
        <button
          class="gps-prompt-close"
          @click="gpsDismissed = true"
        >
          Закрыть
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.map-col {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* ── Map ── */
.map-wrap {
  position: relative;
  flex-shrink: 0;
  isolation: isolate;
  z-index: 0;
  overflow: hidden;
}
.mini-map {
  width: 100%;
  background: #0a0e16;
}

@media (max-width: 899px) {
  .map-col {
    flex: 1 1 0;
    min-height: 0;
  }
  .map-wrap {
    flex: 1 1 0;
    min-height: 0;
  }
  .mini-map {
    position: absolute;
    inset: 0;
    height: auto;
    min-height: 0;
  }
}

@media (min-width: 900px) {
  .map-col {
    height: 100%;
  }
  .map-wrap {
    flex: 1 1 0;
    min-height: 0;
  }
  .mini-map {
    position: absolute;
    inset: 0;
    height: auto;
    min-height: 0;
  }
}
.mini-map.picking {
  cursor: crosshair;
}
.mini-map.picking :deep(.leaflet-container) {
  cursor: crosshair;
}
.pick-hint {
  position: absolute;
  left: 50%;
  bottom: 12px;
  transform: translateX(-50%);
  z-index: 500;
  background: rgba(15, 20, 32, 0.92);
  color: #cde9fb;
  border: 1px solid #2aabee;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
}
:deep(.you-dot) {
  width: 14px;
  height: 14px;
  background: #2aabee;
  border: 3px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 4px rgba(42, 171, 238, 0.3);
}

/* ── GPS prompt ── */
.gps-prompt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  background: #2a2438;
  font-size: 13px;
  color: #d9c7f0;
  flex-shrink: 0;
}
.gps-prompt-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.gps-prompt button {
  background: #7c4dff;
  color: #fff;
  border: none;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}
.gps-prompt-close {
  background: transparent;
  border: 1px solid #5a4a78;
  color: #b9a7d0;
}
</style>

<!-- Non-scoped: the live-track head marker is HTML injected by Leaflet (outside the component's
     scoped DOM), and a scoped @keyframes name would be mangled out of sync with this rule. -->
<style>
.track-head {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.track-head.track-pulse {
  animation: radar-track-pulse 1.4s ease-out infinite;
}
@keyframes radar-track-pulse {
  0% { filter: drop-shadow(0 0 2px currentColor); transform: scale(0.9); }
  50% { filter: drop-shadow(0 0 8px currentColor); transform: scale(1.15); }
  100% { filter: drop-shadow(0 0 2px currentColor); transform: scale(0.9); }
}
</style>
